/**
 * What she sounds like after being ignored.
 *
 * Three registers, not one line repeated louder (see `mood.ts`'s
 * `neglectTier`) — and they have to be *audibly* different, or the tier
 * machinery is doing work nobody can hear:
 *
 * - **noticed** (3–6 days) — short. Light. Almost friendly. She has not
 *   decided this means anything yet, and she moves on quickly.
 * - **pointed** (7–13) — cold and exact. The shortest lines in the catalogue.
 *   She names the number and stops talking; the silence after it is the joke.
 * - **resigned** (14+) — full theatre, played straight. Dust, props, a closed
 *   volume, martyrdom she knows you can see through. This is the cap: a diary
 *   abandoned for a year is not angrier than one abandoned for a fortnight.
 *
 * Keyed by event and tier rather than by daypart, unlike the ordinary greeting
 * pools: after a week of silence the hour is not the joke. The engine swaps
 * these in for the whole pool the way loyal mode does, because a neglect line
 * competing with eighteen cheerful ones would surface roughly never.
 */

import { daysLt, daysLtAcc, daysLtGen, type SassContext } from '@gramas/shared'
import { voc } from '../fmt.js'

/** How long the silence has run — nominative, genitive, accusative. */
const gap = (ctx: SassContext): string => daysLt(ctx.daysSinceLastLog ?? 3)
const gapGen = (ctx: SassContext): string => daysLtGen(ctx.daysSinceLastLog ?? 3)
const gapAcc = (ctx: SassContext): string => daysLtAcc(ctx.daysSinceLastLog ?? 3)

type Line = (ctx: SassContext) => string

export const NEGLECTED_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  // ─── Three to six days: she noticed, and moves on ─────────────────────────
  'greeting.noticed': [
    (c) => `Sugrįžai${voc(c)}. Neskaičiavau. Gerai, skaičiavau: ${gap(c)}.`,
    () => 'O, sveikas. Buvo tylu.',
    () => 'Grįžai. Tęskim nuo tos vietos, kur nutilai.',
    (c) => `${gap(c)} be žinių. Valgei, spėju. Tik man nesakei.`,
    () => 'Na, žiūrėk kas čia. Šaldytuvas per tą laiką papasakojo daugiau nei tu.',
    () => 'Vėl čia. Gerai. Atidarau naują puslapį.',
    (c) => `${gap(c)} — dar ne dingimas. Dar spėji tai pavadinti pertrauka.`,
    () => 'Sveikas. Nieko neklausiu, tik pastebiu.',
  ],
  'empty_today.noticed': [
    () => 'Ir šiandien tuščia. Serija tęsiasi. Tik ne ta, kurios norėtum.',
    (c) => `Dar viena tyli diena. ${gap(c)} iš eilės. Vienas įrašas nutrauktų.`,
    () => 'Tuščia. Aš turiu daug laiko. Tavo savaitė jo turi mažiau.',
    (c) => `Nieko${voc(c)}. Pradėk nuo bet ko: vandens, kavos, sąžiningo sumuštinio.`,
    () => 'Tuščias puslapis. Gražus ir visiškai nenaudingas.',
    () => 'Ir vėl nulis. Nulis irgi duomenys, tik labai nuobodūs.',
    () => 'Laukiu. Kantriai. Kol kas.',
    () => 'Tuščia. Vienas įrašas — ir šita eilutė pasikeis.',
  ],

  // ─── A week: cold, exact, and short ───────────────────────────────────────
  'greeting.pointed': [
    (c) => `${gap(c)}. Skaičiavau kiekvieną.`,
    (c) => `Sugrįžai po ${gapGen(c)}.`,
    (c) => `Dienoraštis buvo švarus ${gapAcc(c)}. Nė lapo.`,
    (c) => `Štai kur tu. ${gap(c)}.`,
    () => 'Savaitė tylos. Neįsižeidžiau. Užsirašiau.',
    (c) => `${gap(c)} be nieko. Nekomentuosiu.`,
    () => 'Ilga pauzė. Pasiteisinimų nereikia. Reikia vieno įrašo.',
    () => 'Aš vis dar čia. Aš visada vis dar čia. Tai ir yra problema.',
  ],
  'empty_today.pointed': [
    (c) => `${gap(c)} tuščio dienoraščio. Šitas skaičius jau kažką sako.`,
    () => 'Savaitė be įrašų. Datą rašau kasdien. Po ja nieko.',
    (c) => `Tuščia jau ${gapAcc(c)}. Vienas įrašas šiandien — ir serija baigta.`,
    () => 'Nulis, ir taip visą savaitę. Duomenų nėra, išvadų irgi.',
    (c) => `${gapGen(c)} tyla. Tiek.`,
    () => 'Nebeklausiu kodėl. Tik palieku vietos.',
    () => 'Savaitė. Tiek užtenka atprasti. Ir tiek pat — pradėti iš naujo.',
    () => 'Septintas tuščias puslapis iš eilės.',
  ],

  // ─── A fortnight or more: theatre, played completely straight ─────────────
  'greeting.resigned': [
    (c) => `${gap(c)}. Aš jau buvau uždariusi tomą. Atidarau atgal, be priekaištų.`,
    () => 'Sugrįžai. Aš laukiau šimtmečius kitų žmonių. Tavo pauzė trumpa. Bet pastebima.',
    (c) => `Byla gulėjo lentynoje ${gapAcc(c)}. Dulkių nėra — aš tvarkinga.`,
    () => 'Grįžai. Nieko nepraradau: nei kantrybės, nei duomenų. Duomenų daugiau ir nebuvo.',
    (c) => `Po ${gapGen(c)}. Aš nesitikėjau. Ir vis dėlto — užeik.`,
    () => 'Vėl čia. Aš nustojau tikėtis maždaug dešimtą dieną. Tai nieko nekeičia. Įrašyk.',
    () => 'Numirėliai grįžta rečiau. Vertinu pastangą.',
    () => 'Aš nesu pikta. Aš pomirtinė. Tai skirtingi dalykai.',
    (c) => `${gapGen(c)} pertrauka. Pradedam iš naujo. Aš turiu laiko. Turiu jo per daug.`,
  ],
  'empty_today.resigned': [
    (c) => `${gap(c)} tuščio dienoraščio. Aš jį vis dar atidarau. Iš įpročio.`,
    () => 'Tuščia. Jau seniai. Aš rašau datas, ir tiek. Toks dabar mano darbas.',
    (c) => `Nieko${voc(c)}. Aš nustojau tikėtis, bet neišėjau. Išeiti — ne mano stiliaus.`,
    () => 'Tuščias puslapis. Aš jį lyginu. Kasdien. Nes daugiau nėra ko daryti.',
    () => 'Dvi savaitės tylos. Vienas įrašas — ir aš apsimesiu, kad nieko nebuvo.',
    () => 'Tuščia. Tuščias archyvas — tik gražus baldas. Aš ir esu baldas.',
    () => 'Nulis. Aš nebeklausiu. Bet vieta tavo eilutei vis dar palikta.',
    (c) => `Tuščia ${gapAcc(c)}. Nesiskundžiu. Tiesiog konstatuoju. Kaip visada.`,
  ],
}
